import React, { memo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import {
  InputBase,
  Paper,
  ClickAwayListener,
  Typography,
  CircularProgress,
} from '@material-ui/core';

import SelectComp from '../../SelectComp';
import Style from './Style';
import { searchVehicle } from '../../../actions/vehicle/vehicle-actions';

const useStyles = makeStyles((theme) => Style(theme));

const VehicleListSearch = ({
  setSelectedVehicle,
  vehicleWithNoCustomer = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [searchType, setSearchType] = useState('plate');
  const [searchValue, setSearchValue] = useState('');
  const [showVehicles, setShowVehicles] = useState(false);

  const searchedVehicles = useSelector(
    (state) => state.vehicle?.searchedVehicles || [],
  );

  const searchVehicleLoading = useSelector(
    (state) => state.vehicle?.searchVehicleLoading || false,
  );

  const handleChangeSearch = _.debounce(async (value) => {
    if (!showVehicles) {
      setShowVehicles(true);
    }
    if (searchType && value) {
      dispatch(
        searchVehicle({
          searchType,
          searchValue: value,
          vehicleWithNoCustomer,
        }),
      );
    } else {
      setShowVehicles(false);

      dispatch({ type: 'CLEAR_SEARCH_VEHICLE_STATE' });
    }
  }, 500);

  const handleSearchVehicles = useCallback(
    (value) => {
      setSearchValue(value);
      handleChangeSearch(value);
    },
    [searchType],
  );

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowVehicles(false);
    setSearchValue('');
    setSearchType('plate');
    dispatch({ type: 'CLEAR_SEARCH_VEHICLE_STATE' });
  };

  return (
    <>
      <Paper className={clsx(classes.root)}>
        <SelectComp
          usedIn='vehicleSearch'
          handleChange={(type) => {
            setSearchType(type);
            dispatch({ type: 'CLEAR_SEARCH_VEHICLE_STATE' });
            setSearchValue('');
          }}
          searchType={searchType}
          menuItems={[
            { label: 'Search Plate', value: 'plate' },
            { label: 'Search VIN', value: 'vin' },
            { label: 'Manual Search', value: 'manual' },
            { label: 'MC Number', value: 'mcNumber' },
            { label: 'Unit Number', value: 'unitNumber' },
          ]}
        />
        <InputBase
          inputProps={{ 'aria-label': 'search vehicle' }}
          className={classes.input}
          onChange={(e) => {
            handleSearchVehicles(e.target.value?.trimStart());
          }}
          onFocus={() => {
            if (searchValue && !showVehicles) {
              setShowVehicles(true);
            }
          }}
          value={searchValue || ''}
          autoFocus
          placeholder={
            (searchType === 'vin' && 'Example : ABCD123456789') ||
            (searchType === 'plate' && 'Example : JL430256') ||
            (searchType === 'manual' && 'Example : Year, Make, Model') ||
            (searchType === 'mcNumber' && 'Example : MC-53368') ||
            (searchType === 'unitNumber' && 'Example : 1KJH53368')
          }
          autoComplete='off'
        />

        {showVehicles ? (
          searchVehicleLoading ? (
            <div
              className={clsx(classes.root__dropdown__main, 'search__dropdown')}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress size={20} />
            </div>
          ) : (
            <ClickAwayListener
              onClickAway={() => {
                setShowVehicles(false);
              }}
            >
              {searchedVehicles?.length ? (
                <div
                  className={clsx(
                    classes.root__dropdown__main,
                    'search__dropdown',
                  )}
                >
                  {searchedVehicles?.map((vehicle, key) => {
                    return (
                      <div
                        className={clsx(
                          'd-flex flex-col',
                          'c-pointer',
                          classes.list__item,
                        )}
                        onClick={() => handleSelectVehicle(vehicle)}
                      >
                        <Typography
                          className={clsx(
                            classes.dropdown__menu__text__heading,
                          )}
                          key={key++}
                        >
                          {(vehicle?.modelYear || '-') +
                            ' ' +
                            (vehicle?.vehicleMake || '-') +
                            ' ' +
                            (vehicle?.vehicleModel || '-')}
                        </Typography>
                        <div className='d-flex flex-col space-between'>
                          <Typography
                            className={clsx(classes.dropdown__menu__text)}
                            key={key++}
                          >
                            License Plate :
                            <span style={{ fontWeight: 400 }}>
                              {vehicle.licensePlate}
                            </span>
                          </Typography>
                          <Typography
                            className={clsx(classes.dropdown__menu__text)}
                            key={key++}
                          >
                            Owner:
                            <span style={{ fontWeight: 400 }}>
                              {vehicle?.customer?.name || '-'}
                            </span>
                          </Typography>
                          <Typography
                            className={clsx(classes.dropdown__menu__text)}
                            key={key++}
                          >
                            Phone:
                            <span style={{ fontWeight: 400 }}>
                              {vehicle?.customer?.phone || '-'}
                            </span>
                          </Typography>
                          <Typography
                            className={clsx(classes.dropdown__menu__text)}
                            key={key++}
                          >
                            USDOT Number:
                            <span style={{ fontWeight: 400 }}>
                              {vehicle?.customer?.usDotNumber || '-'}
                            </span>
                          </Typography>
                          <Typography
                            className={clsx(classes.dropdown__menu__text)}
                            key={key++}
                          >
                            PST Number:
                            <span style={{ fontWeight: 400 }}>
                              {vehicle?.customer?.pstNumber || '-'}
                            </span>
                          </Typography>
                          <Typography
                            className={clsx(classes.dropdown__menu__text)}
                            key={key++}
                          >
                            MC Number:
                            <span style={{ fontWeight: 400 }}>
                              {vehicle?.mcNumber || '-'}
                            </span>
                          </Typography>
                          <Typography
                            className={clsx(classes.dropdown__menu__text)}
                            key={key++}
                          >
                            Unit Number:
                            <span style={{ fontWeight: 400 }}>
                              {vehicle?.unitNumber || '-'}
                            </span>
                          </Typography>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  className={clsx(
                    classes.root__dropdown__main,
                    'search__dropdown',
                  )}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    className={clsx(classes.dropdown__menu__text)}
                    style={{
                      color: '#000000',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '19px',
                    }}
                  >
                    Result Not Found!
                  </Typography>
                </div>
              )}
            </ClickAwayListener>
          )
        ) : (
          ''
        )}
      </Paper>
    </>
  );
};

export default memo(VehicleListSearch);
